import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { SearchableSelect, SearchableSelectOption } from '../../../shared/components';
import { useToast } from '../../notifications';
import {
  getKnowledgeDocumentAccess,
  listKnowledgeAccessSubjects,
  updateKnowledgeDocumentAccess,
} from '../services/knowledgeService';
import {
  KnowledgeAccessSubjectOption,
  KnowledgeAccessSubjectType,
  KnowledgeAccessSubjects,
  KnowledgeDocument,
  KnowledgeDocumentAccess,
  isStructuredKnowledgeDocument,
} from '../types/Knowledge';
import styles from './KnowledgeDocumentAccessModal.module.css';

export interface KnowledgeDocumentAccessModalProps {
  document: KnowledgeDocument;
  canManage: boolean;
  onClose: () => void;
  onSaved: (access: KnowledgeDocumentAccess) => void;
}

const EMPTY_SUBJECTS: KnowledgeAccessSubjects = { roles: [], departments: [], employees: [] };

const toOptions = (items: KnowledgeAccessSubjectOption[]): SearchableSelectOption[] =>
  items.map(item => ({
    value: item.subjectId,
    label: item.displayName,
    meta: item.detail ?? undefined,
  }));

const buildGrants = (
  roleIds: string[],
  departmentIds: string[],
  employeeIds: string[],
): Array<{ subjectType: KnowledgeAccessSubjectType; subjectId: string }> => [
  ...roleIds.map(subjectId => ({ subjectType: 'role' as const, subjectId })),
  ...departmentIds.map(subjectId => ({ subjectType: 'department' as const, subjectId })),
  ...employeeIds.map(subjectId => ({ subjectType: 'employee' as const, subjectId })),
];

export const KnowledgeDocumentAccessModal: React.FC<KnowledgeDocumentAccessModalProps> = ({
  document,
  canManage,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation(['knowledge', 'common']);
  const { showToast } = useToast();
  const dialogRef = React.useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [subjects, setSubjects] = React.useState<KnowledgeAccessSubjects>(EMPTY_SUBJECTS);
  const [isRestricted, setIsRestricted] = React.useState(document.isAccessRestricted);
  const [roleIds, setRoleIds] = React.useState<string[]>([]);
  const [departmentIds, setDepartmentIds] = React.useState<string[]>([]);
  const [employeeIds, setEmployeeIds] = React.useState<string[]>([]);
  const [missingGrants, setMissingGrants] = React.useState<string[]>([]);

  const isStructured = isStructuredKnowledgeDocument(document);
  const isInternal = document.visibility === 'internal';
  const canRestrict = canManage && !isStructured && isInternal;
  const grantCount = roleIds.length + departmentIds.length + employeeIds.length;
  const saveDisabled = saving || loading || !canManage || (isRestricted && (grantCount === 0 || !canRestrict));

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [access, subjectOptions] = await Promise.all([
          getKnowledgeDocumentAccess(document.id),
          canManage ? listKnowledgeAccessSubjects() : Promise.resolve(EMPTY_SUBJECTS),
        ]);

        if (cancelled) {
          return;
        }

        setSubjects(subjectOptions);
        setIsRestricted(access.isRestricted);
        setRoleIds(access.grants.filter(grant => grant.subjectType === 'role').map(grant => grant.subjectId));
        setDepartmentIds(access.grants.filter(grant => grant.subjectType === 'department').map(grant => grant.subjectId));
        setEmployeeIds(access.grants.filter(grant => grant.subjectType === 'employee').map(grant => grant.subjectId));
        setMissingGrants(access.grants.filter(grant => !grant.subjectExists).map(grant => grant.displayName));
      } catch (error) {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : t('access.failedToLoad', { ns: 'knowledge' }));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [canManage, document.id, t]);

  React.useEffect(() => {
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();

    return () => {
      window.document.body.style.overflow = previousOverflow;
    };
  }, []);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) {
        onClose();
      }
    };

    window.document.addEventListener('keydown', onKeyDown);
    return () => {
      window.document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, saving]);

  const onSave = async () => {
    if (saveDisabled) {
      return;
    }

    setSaving(true);
    try {
      const access = await updateKnowledgeDocumentAccess(document.id, {
        isRestricted,
        grants: isRestricted ? buildGrants(roleIds, departmentIds, employeeIds) : [],
      });
      showToast(t('access.saved', { ns: 'knowledge' }), 'success');
      onSaved(access);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('access.failedToSave', { ns: 'knowledge' }), 'error');
    } finally {
      setSaving(false);
    }
  };

  const roleOptions = React.useMemo(() => toOptions(subjects.roles), [subjects.roles]);
  const departmentOptions = React.useMemo(() => toOptions(subjects.departments), [subjects.departments]);
  const employeeOptions = React.useMemo(() => toOptions(subjects.employees), [subjects.employees]);

  const documentName = document.title?.trim() || document.originalFileName;

  const renderPicker = (
    label: string,
    hint: string,
    options: SearchableSelectOption[],
    value: string[],
    onChange: (next: string[]) => void,
    testId: string,
  ) => (
    <div className={styles.pickerGroup}>
      <div className={styles.pickerHeader}>
        <span className={styles.pickerLabel}>{label}</span>
        <span className={styles.pickerCount}>{value.length}</span>
      </div>
      <p className={styles.pickerHint}>{hint}</p>
      <div data-testid={testId}>
        <SearchableSelect
          multiple
          value={value}
          onChange={onChange}
          options={options}
          disabled={!canRestrict || saving}
          maxSuggestions={80}
          placeholder={t('access.searchPlaceholder', { ns: 'knowledge' })}
          noOptionsText={t('access.noOptions', { ns: 'knowledge' })}
          ariaLabel={label}
        />
      </div>
    </div>
  );

  return createPortal(
    <div
      className={styles.overlay}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="knowledge-access-title"
        tabIndex={-1}
      >
        <div className={styles.header}>
          <div>
            <h2 id="knowledge-access-title" className={styles.title}>
              {t('access.title', { ns: 'knowledge' })}
            </h2>
            <p className={styles.subtitle}>{documentName}</p>
          </div>
          <button
            type="button"
            className={styles.close}
            aria-label={t('close', { ns: 'common' })}
            onClick={onClose}
            disabled={saving}
          >
            ×
          </button>
        </div>

        <div className={styles.body}>
          {loading ? (
            <p className={styles.state}>{t('loading', { ns: 'common' })}</p>
          ) : loadError ? (
            <p className={`${styles.state} ${styles.stateError}`}>{loadError}</p>
          ) : (
            <>
              <label className={`${styles.toggleRow} ${!canRestrict ? styles.toggleRowDisabled : ''}`}>
                <input
                  type="checkbox"
                  checked={isRestricted}
                  disabled={!canManage || saving || (!canRestrict && !isRestricted)}
                  onChange={(event) => setIsRestricted(event.target.checked)}
                />
                <span>
                  <span className={styles.toggleTitle}>{t('access.restrictToggle', { ns: 'knowledge' })}</span>
                  <span className={styles.toggleHint}>{t('access.restrictHint', { ns: 'knowledge' })}</span>
                </span>
              </label>

              {!canManage ? (
                <p className={styles.notice}>{t('access.readOnlyNotice', { ns: 'knowledge' })}</p>
              ) : null}

              {isStructured ? (
                <p className={`${styles.notice} ${styles.noticeWarning}`}>{t('access.structuredNotice', { ns: 'knowledge' })}</p>
              ) : !isInternal ? (
                <p className={`${styles.notice} ${styles.noticeWarning}`}>{t('access.requiresInternalNotice', { ns: 'knowledge' })}</p>
              ) : null}

              {missingGrants.length > 0 ? (
                <p className={`${styles.notice} ${styles.noticeWarning}`}>
                  {t('access.missingSubjects', { ns: 'knowledge', items: missingGrants.join(', ') })}
                </p>
              ) : null}

              {isRestricted ? (
                <div className={styles.pickers}>
                  {renderPicker(
                    t('access.roles', { ns: 'knowledge' }),
                    t('access.rolesHint', { ns: 'knowledge' }),
                    roleOptions,
                    roleIds,
                    setRoleIds,
                    'knowledge-access-roles',
                  )}
                  {renderPicker(
                    t('access.departments', { ns: 'knowledge' }),
                    t('access.departmentsHint', { ns: 'knowledge' }),
                    departmentOptions,
                    departmentIds,
                    setDepartmentIds,
                    'knowledge-access-departments',
                  )}
                  {renderPicker(
                    t('access.employees', { ns: 'knowledge' }),
                    t('access.employeesHint', { ns: 'knowledge' }),
                    employeeOptions,
                    employeeIds,
                    setEmployeeIds,
                    'knowledge-access-employees',
                  )}
                  {grantCount === 0 ? (
                    <p className={`${styles.notice} ${styles.noticeWarning}`}>{t('access.atLeastOne', { ns: 'knowledge' })}</p>
                  ) : null}
                </div>
              ) : (
                <p className={styles.notice}>{t('access.openNotice', { ns: 'knowledge' })}</p>
              )}

              <p className={styles.footnote}>{t('access.alwaysVisibleNote', { ns: 'knowledge' })}</p>
            </>
          )}
        </div>

        <div className={styles.footer}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            {canManage ? t('cancel', { ns: 'common' }) : t('close', { ns: 'common' })}
          </button>
          {canManage ? (
            <button type="button" className="btn btn-primary" onClick={() => void onSave()} disabled={saveDisabled}>
              {saving ? t('working', { ns: 'knowledge' }) : t('save', { ns: 'common' })}
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    window.document.body,
  );
};
